import * as Schema from "effect/Schema";
import { PositiveInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

const PROJECT_SEARCH_ENTRIES_MAX_LIMIT = 200;
const PROJECT_RELATIVE_PATH_MAX_LENGTH = 512;

export const ProjectSearchEntriesInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  query: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  limit: PositiveInt.check(Schema.isLessThanOrEqualTo(PROJECT_SEARCH_ENTRIES_MAX_LIMIT)),
});
export type ProjectSearchEntriesInput = typeof ProjectSearchEntriesInput.Type;

const ProjectEntryKind = Schema.Literals(["file", "directory"]);

export const ProjectEntry = Schema.Struct({
  path: TrimmedNonEmptyString,
  kind: ProjectEntryKind,
  parentPath: Schema.optional(TrimmedNonEmptyString),
});
export type ProjectEntry = typeof ProjectEntry.Type;

export const ProjectSearchEntriesResult = Schema.Struct({
  entries: Schema.Array(ProjectEntry),
  truncated: Schema.Boolean,
});
export type ProjectSearchEntriesResult = typeof ProjectSearchEntriesResult.Type;

export class ProjectSearchEntriesError extends Schema.TaggedErrorClass<ProjectSearchEntriesError>()(
  "ProjectSearchEntriesError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ProjectDocumentsStatusInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type ProjectDocumentsStatusInput = typeof ProjectDocumentsStatusInput.Type;

export const ProjectDocumentState = Schema.Literals([
  "ok",
  "unparsed",
  "stale",
  "failed",
  "orphaned",
  "unsupported",
]);
export type ProjectDocumentState = typeof ProjectDocumentState.Type;

export const ProjectDocumentUsage = Schema.Struct({
  model: Schema.NullOr(Schema.String),
  llmCalls: Schema.Number,
  inputTokens: Schema.Number,
  outputTokens: Schema.Number,
  thinkingTokens: Schema.Number,
  cachedTokens: Schema.Number,
  totalTokens: Schema.Number,
  costUsd: Schema.NullOr(Schema.Number),
});
export type ProjectDocumentUsage = typeof ProjectDocumentUsage.Type;

export const ProjectDocumentFile = Schema.Struct({
  path: TrimmedNonEmptyString,
  state: ProjectDocumentState,
  restorable: Schema.Boolean,
  sha256: Schema.NullOr(Schema.String),
  size: Schema.NullOr(Schema.Number),
  mtimeMs: Schema.NullOr(Schema.Number),
  parsedAt: Schema.NullOr(Schema.String),
  pageCount: Schema.NullOr(Schema.Number),
  usage: Schema.NullOr(ProjectDocumentUsage),
  resultPath: Schema.NullOr(Schema.String),
  error: Schema.NullOr(Schema.String),
});
export type ProjectDocumentFile = typeof ProjectDocumentFile.Type;

export const ProjectDocumentsUsageTotal = Schema.Struct({
  docs: Schema.Number,
  pages: Schema.Number,
  llmPages: Schema.Number,
  llmCalls: Schema.Number,
  inputTokens: Schema.Number,
  outputTokens: Schema.Number,
  thinkingTokens: Schema.Number,
  cachedTokens: Schema.Number,
  totalTokens: Schema.Number,
  costUsd: Schema.Number,
});
export type ProjectDocumentsUsageTotal = typeof ProjectDocumentsUsageTotal.Type;

export const ProjectUsageModelBreakdown = Schema.Struct({
  model: TrimmedNonEmptyString,
  docs: Schema.Number,
  llmCalls: Schema.Number,
  inputTokens: Schema.Number,
  outputTokens: Schema.Number,
  thinkingTokens: Schema.Number,
  cachedTokens: Schema.Number,
  totalTokens: Schema.Number,
  costUsd: Schema.Number,
});
export type ProjectUsageModelBreakdown = typeof ProjectUsageModelBreakdown.Type;

export const ProjectsUsageReportInput = Schema.Struct({
  cwds: Schema.Array(TrimmedNonEmptyString),
});
export type ProjectsUsageReportInput = typeof ProjectsUsageReportInput.Type;

export const ProjectUsageRow = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  usage: ProjectDocumentsUsageTotal,
  models: Schema.Array(ProjectUsageModelBreakdown),
});
export type ProjectUsageRow = typeof ProjectUsageRow.Type;

export const ProjectsUsageReportResult = Schema.Struct({
  rows: Schema.Array(ProjectUsageRow),
  total: ProjectDocumentsUsageTotal,
  totalModels: Schema.Array(ProjectUsageModelBreakdown),
});
export type ProjectsUsageReportResult = typeof ProjectsUsageReportResult.Type;

export class ProjectsUsageReportError extends Schema.TaggedErrorClass<ProjectsUsageReportError>()(
  "ProjectsUsageReportError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ProjectDocumentsCounts = Schema.Struct({
  ok: Schema.Number,
  unparsed: Schema.Number,
  stale: Schema.Number,
  failed: Schema.Number,
  orphaned: Schema.Number,
  unsupported: Schema.Number,
});
export type ProjectDocumentsCounts = typeof ProjectDocumentsCounts.Type;

export const ProjectDocumentsStatusResult = Schema.Struct({
  root: TrimmedNonEmptyString,
  artifactsPrefix: Schema.NullOr(Schema.String),
  counts: ProjectDocumentsCounts,
  usage: ProjectDocumentsUsageTotal,
  files: Schema.Array(ProjectDocumentFile),
});
export type ProjectDocumentsStatusResult = typeof ProjectDocumentsStatusResult.Type;

export class ProjectDocumentsStatusError extends Schema.TaggedErrorClass<ProjectDocumentsStatusError>()(
  "ProjectDocumentsStatusError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ProjectDeleteDocumentInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_RELATIVE_PATH_MAX_LENGTH)),
  reconcile: Schema.optional(Schema.Boolean),
});
export type ProjectDeleteDocumentInput = typeof ProjectDeleteDocumentInput.Type;

export const ProjectDeleteDocumentResult = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
});
export type ProjectDeleteDocumentResult = typeof ProjectDeleteDocumentResult.Type;

export class ProjectDeleteDocumentError extends Schema.TaggedErrorClass<ProjectDeleteDocumentError>()(
  "ProjectDeleteDocumentError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ProjectOutputStatusInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type ProjectOutputStatusInput = typeof ProjectOutputStatusInput.Type;

export const ProjectOutputFile = Schema.Struct({
  path: TrimmedNonEmptyString,
  size: Schema.Number,
  mtimeMs: Schema.NullOr(Schema.Number),
});
export type ProjectOutputFile = typeof ProjectOutputFile.Type;

export const ProjectOutputStatusResult = Schema.Struct({
  root: TrimmedNonEmptyString,
  artifactsPrefix: Schema.NullOr(Schema.String),
  files: Schema.Array(ProjectOutputFile),
});
export type ProjectOutputStatusResult = typeof ProjectOutputStatusResult.Type;

export class ProjectOutputStatusError extends Schema.TaggedErrorClass<ProjectOutputStatusError>()(
  "ProjectOutputStatusError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ProjectCopyOutputToAttachmentsInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_RELATIVE_PATH_MAX_LENGTH)),
  reconcile: Schema.optional(Schema.Boolean),
});
export type ProjectCopyOutputToAttachmentsInput = typeof ProjectCopyOutputToAttachmentsInput.Type;

export const ProjectCopyOutputToAttachmentsResult = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
});
export type ProjectCopyOutputToAttachmentsResult = typeof ProjectCopyOutputToAttachmentsResult.Type;

export class ProjectCopyOutputError extends Schema.TaggedErrorClass<ProjectCopyOutputError>()(
  "ProjectCopyOutputError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ProjectDeleteOutputFileInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_RELATIVE_PATH_MAX_LENGTH)),
});
export type ProjectDeleteOutputFileInput = typeof ProjectDeleteOutputFileInput.Type;

export const ProjectDeleteOutputFileResult = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
});
export type ProjectDeleteOutputFileResult = typeof ProjectDeleteOutputFileResult.Type;

export class ProjectDeleteOutputError extends Schema.TaggedErrorClass<ProjectDeleteOutputError>()(
  "ProjectDeleteOutputError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}
